import { useCommunityContext } from 'providers';
import { useQuery } from 'react-query';

import { ContentV1 } from '@jebel/constants';

import { useCurrentUserCommunityMembership } from 'features/community/hooks';
import { useCurrentUser } from 'shared/hooks';

const CURRENT_USER_CONTENT_RESTRICTIONS_QUERY_KEY = 'CURRENT_USER_CONTENT_RESTRICTIONS';

/**
 * Custom hook to fetch the current user's content restrictions.
 *
 * This hook uses the `useCurrentUserCommunityMembership` hook to get the current user's community membership.
 * It then uses the `useQuery` hook to fetch the current user's content restrictions.
 *
 * @see https://github.com/jebelapp/jebel/issues/1842
 * @returns The current user's content restrictions.
 */
export function useCurrentUserContentRestrictions() {
  const { user } = useCurrentUser();
  const { selectedCommunity: community } = useCommunityContext();
  const { data: membership } = useCurrentUserCommunityMembership();

  const {
    data = [],
    error,
    isLoading,
  } = useQuery({
    enabled: Boolean(user && community && membership),
    queryKey: [CURRENT_USER_CONTENT_RESTRICTIONS_QUERY_KEY],

    async queryFn() {
      const url = new URL(
        `/restriction/community/${community!.id}/user`,
        process.env.REACT_APP_CONTENT_API_URL,
      );

      const affiliation = membership!.affiliation ?? user?.affiliation;

      if (affiliation) {
        url.searchParams.set('affiliation', affiliation);
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch current user content restrictions (HTTP ${response.status})`,
        );
      }

      const data = await response.json();
      return data as ContentV1.ContentRestriction[];
    },
  });

  return {
    data,
    error,
    loading: isLoading,
  };
}
